import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, CheckCircle, Edit, XCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { deleteMDTReport, getMDTReportsById } from '../services/mdtReportService';
import { toast } from 'react-toastify';
import { MDTReportStatus, Patient } from '../types';
import { getPatientById } from '../services/patientService';
import { updateMDTReportStatus } from "../services/mdtReportService";
import Modal from '../components/Common/Modal';

const ReportView: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const from = location.state?.from;
  const patientId = location.state?.patientId;

  const [statusMessage, setStatusMessage] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const [report, setReport] = useState<any>([]);
  const [loading, setLoading] = useState(true);

  const [patient, setPatient] = useState<Patient | null>(null);

  const isDoctor = user?.role === 'DOCTOR';

  const reportStatus = report?.status;

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [mdtToDelete, setMdtToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        const reportData = await getMDTReportsById(id);
        setReport(reportData);

        if (reportData?.patient_id) {
          const patientData = await getPatientById(reportData.patient_id);
          setPatient(patientData);
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, location.key, report?.status]);

  if (loading) return <p>Loading MDT reports...</p>;

  if (!report) {
    return (
      <div className="card">
        <p className="text-gray-600">Report not found.</p>
      </div>
    );
  }

  const canEdit = (isDoctor && ["draft", "approved"].includes(reportStatus));

  const handleStatusChange = async (
    newStatus: MDTReportStatus,
    successMessage: string
  ) => {
    try {
      setIsUpdating(true);

      const result = await updateMDTReportStatus(report.id, newStatus);

      setReport(result.data);
      setStatusMessage(successMessage);

      toast.success(successMessage);
    } catch (error: any) {
      toast.error(
        error?.message ||
        error?.response?.data?.message ||
        "Failed to update report status"
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteClick = (id: number) => {
    setMdtToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!mdtToDelete) return;

    try {
      setIsDeleting(true);

      await deleteMDTReport(mdtToDelete);

      toast.success("MDT report deleted successfully");

      setIsDeleteModalOpen(false);
      setMdtToDelete(null);

      if (from === "patient") {
        navigate(`/patients/${patientId}?tab=mdt-reports`);
      } else {
        navigate("/reports");
      }
    } catch (err: any) {
      console.error(err);
      toast.error(
        err.response?.data?.message || "Failed to delete MDT report"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
         
          <button
            onClick={() => {
              if (patientId) {
                navigate(`/patients/${patientId}?tab=mdt-reports`);
              } else {
                navigate('/reports');
              }
            }}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>

          <div>
            <h1 className="text-2xl font-bold text-gray-900">{report.title}</h1>
            {/* <p className="text-gray-600">Patient: {patient?.forename} {patient?.surname} • Generated On {new Date(report?.createdAt).toLocaleDateString()}</p> */}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {canEdit && (
            <button
              onClick={() => navigate(`/reports/${report.id}/edit`, { state: location.state })}
              className="btn-primary flex items-center space-x-2"
            >
              <Edit className="h-4 w-4" />
              <span>Edit</span>
            </button>
          )}

          <button
            onClick={() => handleDeleteClick(report.id)}
            className="btn-danger"
          >
            Delete
          </button>
        </div>
      </div>

      {statusMessage && (
        <div className="p-3 rounded-md bg-green-50 text-green-800 border border-green-200">{statusMessage}</div>
      )}

      <div className="card space-y-6">
        <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans">
          <ReactMarkdown>{report?.context?.mdt_report || 'MDT report content will appear here.'}</ReactMarkdown>
        </pre>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <span className="text-sm text-gray-600">Status: </span>
          <span className="text-sm font-medium text-gray-900 capitalize">{report.status.replace('_', ' ')}</span>
        </div>
        <div className="flex items-center space-x-2">
          {/* DRAFT */}
          {reportStatus === "draft" && isDoctor && (
            <>
              <button
                disabled={isUpdating}
                onClick={() =>
                  handleStatusChange("approved", "Report approved")
                }
                className="btn-primary flex items-center space-x-2"
              >
                <CheckCircle className="h-4 w-4" />
                <span>Approve</span>
              </button>

              <button
                disabled={isUpdating}
                onClick={() =>
                  handleStatusChange("rejected", "Report rejected")
                }
                className="btn-secondary flex items-center space-x-2"
              >
                <XCircle className="h-4 w-4" />
                <span>Reject</span>
              </button>
            </>
          )}
          
          {/* APPROVED */}
          {report.status === "approved" && (
            <div className="text-sm text-green-600 font-medium flex items-center space-x-1">
              <CheckCircle className="h-4 w-4" />
              <span>Report Approved</span>
            </div>
          )}

          {/* REJECTED */}
          {report.status === "rejected" && (
            <div className="text-sm text-red-600 font-medium flex items-center space-x-1">
              <XCircle className="h-4 w-4" />
              <span>Report Rejected</span>
            </div>
          )}
        </div>

        {/* Delete Modal */}
        <Modal
          title="Delete MDT Report"
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          widthClassName="max-w-md"
        >
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to delete this MDT report?
              <br />
              <span className="text-red-600 font-medium">
                This action cannot be undone.
              </span>
            </p>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="btn-secondary"
                disabled={isDeleting}
              >
                Cancel
              </button>

              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="btn-danger"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default ReportView;


